import React, { useState, useContext, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CardNew from "components/molecules/Card/CardNew";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import getLabel from "helpers/getLabel";
import errorToast from "helpers/errorToast";
import { getBillingAnalytics } from "api";
import AnalyticsContext from "../analyticsContext";
import TableChart from "../TableChart";

function Billing() {
  const intl = useIntl();
  const { params } = useContext(AnalyticsContext);
  const [locationData, setLocationData] = useState([]);
  const [providerData, setproviderData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState("1");

  const tabs = [
    {
      label: intl.formatMessage({
        id: "general.location",
        defaultMessage: "Location",
      }),
      value: "1",
    },
    {
      label: intl.formatMessage({
        id: "general.provider",
        defaultMessage: "Provider",
      }),
      value: "2",
    },
  ];

  const getBillingData = () => {
    setIsLoading(true);
    getBillingAnalytics({ params })
      .then((res) => {
        setLocationData(res.data.location_data);
        setproviderData(res.data.provider_data);
        setIsLoading(false);
      })
      .catch((err) => errorToast(err));
  };

  useEffect(() => getBillingData(), [params]);

  const formatNumber = (number) =>
    Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(number);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="pl-9 ">
            {tabValue === "1" ? (
              <FormattedMessage
                id="general.paymentLocation"
                values={{ paymentLocation: getLabel("Payment location", "uppercase") }}
                defaultMessage="Payment location"
              />
            ) : (
              <FormattedMessage id="general.provider" defaultMessage="Provider" />
            )}
          </span>
        ),
        sortable: true,
        accessor: "0",
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.shipping" defaultMessage="Shipping" />*
          </span>
        ),
        accessor: "1",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <p className="pt-8 pb-8 mb-0">{formatNumber(value)}</p>,
      },
      {
        Header: (
          <span className="mb-0 ">
            <FormattedMessage id="general.unsubmitted" defaultMessage="Unsubmitted" />
            **
          </span>
        ),
        accessor: "2",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <div className="pt-8 pb-8">{formatNumber(value)}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="invoices.awaitingAuditFilter" defaultMessage="Awaiting Audit" />
          </span>
        ),
        accessor: "3",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <div className={`pt-8 pb-8 `}>{formatNumber(value)}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.approved" defaultMessage="Approved" />
          </span>
        ),
        accessor: "4",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <div className={`pt-8 pb-8 `}>{formatNumber(value)}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.declined" defaultMessage="Declined" />
          </span>
        ),
        accessor: "5",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <div className={`pt-8 pb-8 `}>{formatNumber(value)}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="invoices.removedFilter" defaultMessage="Removed" />
          </span>
        ),
        accessor: "6",
        sortable: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <div className={`pt-8 pb-8 `}>{formatNumber(value)}</div>,
      },
    ],
    [tabValue],
  );

  return (
    <>
      <CardNew className="border-radius-top-0">
        <div style={{ width: "140px", display: "inline-block" }}>
          <div className="mb-2">
            <FormattedMessage id="general.report" defaultMessage="Report" />
          </div>
          <Dropdown options={tabs} onChange={(item) => setTabValue(item.value)} defaultValue="1" />
        </div>
      </CardNew>
      {isLoading ? (
        <div className="text-center d-flex align-items-center justify-content-center" style={{ height: "400px" }}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          <TableChart data={tabValue === "1" ? locationData : providerData} columns={columns} isLoading={isLoading} />
          <div className="d-flex flex-column mt-3 text-secondary">
            <span>
              <FormattedMessage
                id="analytics.billing.shippingIncludes"
                defaultMessage="* Shipping includes 'Booked', 'In transit' and 'Incomplete' shipments."
              />
            </span>
            <span>
              <FormattedMessage
                id="analytics.billing.unsubmittedIncludes"
                defaultMessage="** Unsubmitted includes 'Delivered' and 'Canceled' shipments."
              />
            </span>
            <span className="text-danger">
              <FormattedMessage id="analytics.billing.allAmountsInUSD" defaultMessage="*** All amounts in USD" />
            </span>
          </div>
        </>
      )}
    </>
  );
}
export default Billing;
